// src/data/fxRatesClient.js
//
// FX rates and conversion-basis client.
//
//   GET  /api/fx-rates                                    (admin/editor/viewer)
//   POST /api/fx-rates/history/manual                    (admin only)
//
// Convention notes:
//   - Sibling client per the dominant pattern in src/data (leasesClient,
//     leasesPortfolioClient, locationsClient, etc.) rather than folding
//     into operationsClient.js. The fx-rates resource has its own routes
//     and its own future cache; keeping it isolated avoids the drift
//     operationsClient.js already warns about ("ad-hoc calls scattered
//     across services/").
//   - Typed errors are promoted from the standard envelope by
//     errorFromResponse — same wire format as every other endpoint.
//   - Successful writes invalidate the in-session rate map so mounted
//     consumers use the saved conversion basis without a full reload.
//   - Manual FX history writes are audited server-side. The browser does
//     not emit a duplicate audit envelope.

import {
  AuthError,
  NetworkError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
import { invalidateFxRates } from "../services/cacheBus.js";
import { log } from "../utils/logger.js";

// ─── Internal: same fetch wrapper shape as operationsClient ───────────────

async function jsonRequest(url, init) {
  let res;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    });
  } catch (err) {
    log.error("fxRatesClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("fxRatesClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

// ─── getFxRates ───────────────────────────────────────────────────────────

/**
 * Read the latest FX rate per currency. Server returns the most-recent
 * row per currency (DISTINCT ON (currency) ORDER BY currency, as_of DESC),
 * already shaped as { currency, rateToUsd, asOf, source } in camelCase
 * with `asOf` as ISO `YYYY-MM-DD`.
 *
 * @returns {Promise<{ rates: Array<{
 *   currency:   string,
 *   rateToUsd:  string,   // NUMERIC serialized as string by pg
 *   asOf:       string,   // ISO YYYY-MM-DD
 *   source:     string,
 * }>}>}
 * @throws  {NetworkError | AuthError}
 */
export async function getFxRates() {
  return jsonRequest("/api/fx-rates", { method: "GET" });
}

export async function getFxConversionBasis() {
  return jsonRequest("/api/fx-rates/basis", { method: "GET" });
}

export async function getFxBasisTransparency() {
  const response = await jsonRequest("/api/fx-rates", { method: "GET" });
  return {
    basis: response?.basis ?? null,
    rates: Array.isArray(response?.basisRates) ? response.basisRates : [],
    liveFetchedAt: response?.liveSnapshot?.fetchedAt ?? null,
  };
}

export async function previewFxConversionBasis(basis) {
  return jsonRequest("/api/fx-rates/basis/preview", {
    method: "POST",
    body: JSON.stringify(basis),
  });
}

export async function saveFxConversionBasis(basis) {
  const result = await jsonRequest("/api/fx-rates/basis", {
    method: "PUT",
    body: JSON.stringify(basis),
  });
  invalidateFxRates();
  return result;
}

export async function getFxBudgetRates(fiscalYear) {
  const year = Number(fiscalYear);
  if (!Number.isInteger(year)) throw new ValidationError("getFxBudgetRates: fiscalYear is required");
  return jsonRequest(`/api/fx-rates/budget-rates?fiscalYear=${encodeURIComponent(year)}`, { method: "GET" });
}

export async function saveFxBudgetRate({ fiscalYear, currency, rateToUsd, rateDate, source } = {}) {
  const result = await jsonRequest("/api/fx-rates/budget-rates", {
    method: "PUT",
    body: JSON.stringify({ fiscalYear, currency, rateToUsd, rateDate, source }),
  });
  invalidateFxRates();
  return result;
}

export async function deleteFxBudgetRate(fiscalYear, currency) {
  const result = await jsonRequest(
    `/api/fx-rates/budget-rates/${encodeURIComponent(fiscalYear)}/${encodeURIComponent(currency)}`,
    { method: "DELETE" },
  );
  invalidateFxRates();
  return result;
}

export async function saveFxManualHistoryRate({ currency, rateToUsd, rateDate } = {}) {
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    throw new ValidationError("saveFxManualHistoryRate: a 3-letter currency code is required");
  }
  if (rateToUsd == null || rateToUsd === "") {
    throw new ValidationError("saveFxManualHistoryRate: rateToUsd is required");
  }
  if (typeof rateDate !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(rateDate)) {
    throw new ValidationError("saveFxManualHistoryRate: rateDate is required");
  }
  const result = await jsonRequest("/api/fx-rates/history/manual", {
    method: "POST",
    body: JSON.stringify({ currency, rateToUsd, rateDate }),
  });
  invalidateFxRates();
  log.api("fxRatesClient.saveFxManualHistoryRate", {
    currency: result?.currency,
    asOf: result?.asOf,
  });
  return result;
}

// ─── triggerFxLiveFetch ───────────────────────────────────────────────────

/**
 * Admin-only: immediately trigger the nightly FX live-rate fetch job
 * without waiting for the 01:30 UTC cron. Useful when the panel shows
 * "Live rates not yet fetched" and the admin wants live data now.
 *
 * @returns {Promise<{ updated: number, skipped: number }>}
 * @throws  {NetworkError | AuthError}
 */
export async function triggerFxLiveFetch() {
  const result = await jsonRequest("/api/admin/trigger-fx-fetch", {
    method: "POST",
  });
  invalidateFxRates();
  log.api("fxRatesClient.triggerFxLiveFetch", result);
  return result;
}

/**
 * Admin-only: fetch historical FX quotes into the current server database.
 * Successful runs invalidate the shared FX resolution cache.
 */
export async function backfillFxHistory({ from, to } = {}) {
  if (typeof from !== "string" || typeof to !== "string") {
    throw new ValidationError("backfillFxHistory: from and to dates are required");
  }
  const result = await jsonRequest("/api/fx-rates/history/backfill", {
    method: "POST",
    body: JSON.stringify({ from, to }),
  });
  invalidateFxRates();
  log.api("fxRatesClient.backfillFxHistory", {
    from: result?.from,
    to: result?.to,
    databaseName: result?.databaseName,
    inserted: result?.inserted,
  });
  return result;
}

// Re-export the typed errors for callers that import the client.
export { AuthError, NetworkError, ValidationError };
